import * as Actions from '../actions/redFlagActions';
import RedFlag from '../../entities/redFlag';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type RedFlagAction = { 
    type: string; 
    redFlag: RedFlag | null; 
    redFlagId: string | RedFlag; 
    partial: boolean; 
    lastFetch: number; 
    redFlags: RedFlag[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<RedFlag> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {RedFlag[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<RedFlag> | null, firmId: string, userId: string, updates?: RedFlag[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'redFlag', 'redFlags', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<RedFlag>} data
 * @param {RedFlag[]} [updates]
 * @returns {ArrayMap<RedFlag>}
 */
function updateStorage(data: ArrayMap<RedFlag>, updates?: RedFlag[]): ArrayMap<RedFlag> {
	if (logging) console.debug('⮂ redFlagReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {RedFlag[]} [updates]
 * @returns {ArrayMap<RedFlag>}
 */
function updateStorageNoReturn(updates?: RedFlag[]): void {
	if (logging) console.debug('⮂ redFlagReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {RedFlag[]} [data]
 * @returns {ArrayMap<RedFlag>}
 */
function createState(data?: RedFlag[] | ArrayMap<RedFlag>): ArrayMap<RedFlag> {
	if (!data) return ArrayMapRegistry.clone<RedFlag>('redFlag', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<RedFlag>('redFlag', data.map(e => e));
    else return ArrayMapRegistry.clone<RedFlag>('redFlag', data);
}

function redFlags(state: ArrayMap<RedFlag>, action: RedFlagAction): ArrayMap<RedFlag> {
	if (!state) state = createState();

	let st: ArrayMap<RedFlag>;

	switch (action.type) {
		// action.redFlag - the new value of redFlag with id action.id
		// action.redFlagId - id of the target, used if new value is null
		case Actions.REDFLAG_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ redFlagReducer: FETCH_ONE_SUCCESS', action);

			if (action.redFlag === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.redFlagId)) {
				st = state.clone();
				st.set(action.redFlag);
				// state.clear();

				updateStorageNoReturn([action.redFlag]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.redFlags
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.redFlags
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.redFlags
			 !partial, !stateEmpty, fetchEmpty	-> D return redFlags in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge redFlags in state newer than those in action.redFlags
		 */
		case Actions.REDFLAG_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.redFlags.length === 0;
			const fetched = action.redFlags.map(redFlag => new RedFlag({...redFlag, persisted: true}));

			if (logging) console.debug(`⮂ redFlagReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} redFlags with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ redFlagReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ redFlagReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<RedFlag> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: RedFlag[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(redFlag => {
					const current = st.get(redFlag.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && redFlag.status === 'D') {
							// Remove it from state
							toUpdate.push(redFlag);
							st.delete(redFlag);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (redFlag.addedOn > current.addedOn) {
								toUpdate.push(redFlag);
								st.set(redFlag);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (redFlag.status !== 'D') {
							toUpdate.push(redFlag);
							st.set(redFlag);
						}
						else if (logging) console.debug('⮂ redFlagReducer: partialMerge: skipping deleted redFlag not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ redFlagReducer: empty: redFlags', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ redFlagReducer: fullMerge: redFlags', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localRedFlag => {
                    // If this redFlag has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localRedFlag.persisted) {
                        const fetchedRedFlag = st.get(localRedFlag.id);
                        if (!fetchedRedFlag || localRedFlag.addedOn > fetchedRedFlag.addedOn) st.set(localRedFlag);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.REDFLAG_FETCH_ONE_FAILURE:
			console.error('REDFLAG_FETCH_ONE_FAILURE', action.redFlagId, action.err);

			// No update to storage required
			return state;

		case Actions.REDFLAG_FETCH_FAILURE:
			console.error('REDFLAG_FETCH_FAILURE', action.redFlagId, action.err);

			// No update to storage required
			return state;

		case Actions.REDFLAG_RESTORE: {
		    let redFlag = new RedFlag({...action.redFlag});
            st = state.clone();

            const existing = st.get(redFlag.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (redFlag.addedOn >= existing.addedOn) {
                    st.set(redFlag);
                    return updateStorage(st, [redFlag]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (redFlag.status === 'D') return state;

                st.set(redFlag);
                return updateStorage(st, [redFlag]);
            }
        }
            
		case Actions.REDFLAG_SAVE: {
		    // console.debug('⮂ REDFLAG_SAVE reducer', action.redFlag?.id);
		    
			// clone the redFlag
			let redFlag = new RedFlag({...action.redFlag});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new redFlag in the state
			// If this is a create, add to the queue
			const _redFlag = st.get(redFlag.id);

			if (!_redFlag) st.set(redFlag);
			else {
				if (redFlag.addedOn >= _redFlag.addedOn) st.set(redFlag);
			}

			return updateStorage(st, [redFlag]);
		}

		case Actions.REDFLAG_SAVE_MANY: {
		    if (logging) console.debug('⮂ REDFLAG_SAVE_MANY reducer', action.redFlags.map(redFlag => redFlag.id).join(','));
		    
			// clone the redFlags
			let toProcess = action.redFlags.map(redFlag => new RedFlag(redFlag));

			st = state.clone();
			// state.clear();

			const updates: RedFlag[] = [];

			toProcess.forEach(redFlag => {
				// Add or update the new redFlag in the state
				// If this is a create, add to the queue
				const existing = st.get(redFlag.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (redFlag.status !== 'D') {
						st.push(redFlag);
						updates.push(redFlag);
					}
					else if (logging) console.debug('⮂ REDFLAG_SAVE_MANY: skipping deleted redFlag not in state');
				}
				else {
					if (redFlag.addedOn >= existing.addedOn) {
						st.set(redFlag);
						updates.push(redFlag);
					}
					else if (logging) console.debug('⮂ Skipping, existing redFlag had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.REDFLAG_REMOVE: {
			// clone the redFlag
			const redFlag = new RedFlag({...action.redFlag});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(redFlag);

			return updateStorage(st, [redFlag]);
		}

		case Actions.REDFLAG_RESTORE_FAILURE:
		case Actions.REDFLAG_SAVE_FAILURE: {
		    console.warn('REDFLAG_SAVE_FAILURE reducer', action.redFlag?.id);
		    if (!action.redFlag?.id) {
				console.warn('REDFLAG_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const redFlag = ensure(RedFlag, action.redFlag);
			if (!redFlag) {
				console.error('Could not find redFlag after call to save');
				return state;
			}

			st = state.clone();

			const _redFlagRef = state.get(redFlag.id);

			if (_redFlagRef && (_redFlagRef.addedOn === redFlag.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _redFlag = new RedFlag({..._redFlagRef});
				_redFlag.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_redFlag.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('redFlag', _redFlag).then(r => {
						if (r.isErr()) console.error(`⮂ redFlagReducer: failed to save redFlag with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ redFlagReducer: saved redFlag with fatalError info');
					}).catch(err => console.error(`⮂ redFlagReducer: failed to save redFlag with fatalError info: ${err.message}`));

				st.set(_redFlag);
			}

			return updateStorage(st);
		}

		case Actions.REDFLAG_SAVE_MANY_FAILURE: {
		    console.warn('REDFLAG_SAVE_MANY_FAILURE reducer', action.redFlags.map(redFlag => redFlag.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.REDFLAG_RESTORE_SUCCESS:
		case Actions.REDFLAG_SAVE_SUCCESS: {
			if (!action.redFlag?.id) {
				console.warn('REDFLAG_SAVE_SUCCESS reducer');
                return state;
			}

			const redFlagRef = state.get(action.redFlag?.id);
			if (!redFlagRef) {
				console.warn('Could not find redFlag after call to save');
                return state;
			}

			st = state.clone();

			if (action.redFlag.addedOn >= redFlagRef.addedOn) {
				st.set(action.redFlag);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const redFlag = new RedFlag({...redFlagRef, persisted: true});
				st.set(redFlag);
			}

			return updateStorage(st, [action.redFlag]);
		}

		case Actions.REDFLAG_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ REDFLAG_SAVE_MANY_SUCCESS reducer', action.redFlags.map(redFlag => redFlag.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const redFlags: RedFlag[] = [];

			action.redFlags.forEach(e => {
				const redFlagRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!redFlagRef) console.warn('Could not find redFlag after call to save');
				else if (e.addedOn >= redFlagRef.addedOn) {
				    st.set(e);
				    redFlags.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const redFlag = new RedFlag({...redFlagRef, persisted: true});
					st.set(redFlag);
					redFlags.push(redFlag);
				}
			});

			return updateStorage(st, redFlags);
		}

		case Actions.REDFLAG_REMOVE_FAILURE: {
		    console.warn('REDFLAG_REMOVE_FAILURE action', action.redFlag?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.REDFLAG_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.REDFLAG_RESTORE_MANY: {
			if (logging) console.debug('⮂ REDFLAG_RESTORE_MANY reducer', action.redFlags.map(redFlag => redFlag.id).join(','));

			const toProcess = action.redFlags.map(redFlag => new RedFlag({...redFlag}));
			st = state.clone();

			const updates: RedFlag[] = [];

			toProcess.forEach(redFlag => {
				const existing = st.get(redFlag.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (redFlag.addedOn >= existing.addedOn) {
						st.set(redFlag);
						updates.push(redFlag);
					}
					else if (logging) console.debug('⮂ REDFLAG_RESTORE_MANY: skipping older version of redFlag');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (redFlag.status !== 'D') {
						st.set(redFlag);
						updates.push(redFlag);
					}
					else if (logging) console.debug('⮂ REDFLAG_RESTORE_MANY: skipping deleted redFlag not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.REDFLAG_REMOVE_MANY: {
			if (logging) console.debug('⮂ REDFLAG_REMOVE_MANY reducer', action.redFlags.map(redFlag => redFlag.id).join(','));

			const toProcess = action.redFlags.map(redFlag => new RedFlag({...redFlag}));
			st = state.clone();

			toProcess.forEach(redFlag => {
				st.delete(redFlag);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default redFlags;